<svelte:options runes={true} />

<script lang="ts" module>
	type Mode = "grid" | "list";
</script>

<script lang="ts">
	import deepEqual from "deep-equal";
	import { Gallery, Spinner, Table, TableBody, TableHead, TableHeadCell } from "flowbite-svelte";
	import { Search } from "flowbite-svelte-v1";
	import { getContext, onMount } from "svelte";
	import { SvelteSet } from "svelte/reactivity";
	import { type Writable } from "svelte/store";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import type { Suite } from "#gen/bundles/v1/bundle_pb.js";
	import { Order, PaginationArgumentsSchema, type PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
	import { FolderSchema, type Folder } from "#gen/folder/v1/folders_pb.js";
	import type { User, Workspace } from "#gen/identities/v1/identities_pb.js";
	import type { ListStaticAssetsResponse, StaticAsset } from "#gen/staticassets/v1/static_assets_pb.js";
	import { DEFAULT_PAGE_SIZE } from "#lib/api/api.js";
	import { ASSETS_FOLDER_PATH, enterFolder, type FolderClient, type FolderList } from "#lib/api/folder.js";
	import type { IdentitiesClient } from "#lib/api/identities.js";
	import { createNextPageArguments } from "#lib/api/pagination.js";
	import type { StaticAssetClient } from "#lib/api/static-assets.js";
	import { isStaticAsset } from "#lib/bulk.js";
	import InfiniteScroll from "#lib/components/InfiniteScroll.svelte";
	import P from "#lib/components/P.svelte";
	import SortableTableHeadCell from "#lib/components/SortableTableHeadCell.svelte";
	import DisplayToggle from "#lib/components/brand/DisplayToggle.svelte";
	import AssetFolderPath from "#lib/components/files/AssetFolderPath.svelte";
	import { UPLOADABLE_DOCUMENT_TYPES, UPLOADABLE_IMAGE_FILE_TYPES, UPLOADABLE_MOVIE_TYPES } from "#lib/components/images/constants.js";
	import FileExplorerTableRow from "#lib/components/images/file-management/FileExplorerTableRow.svelte";
	import FileExplorerTile from "#lib/components/images/file-management/FileExplorerTile.svelte";
	import FileTypeFilter from "#lib/components/images/file-management/FileTypeFilter.svelte";
	import { autofocus } from "#lib/directives/autofocus.js";
	import { Logger } from "#lib/logging.js";
	import { create } from "#lib/protobuf-compat.js";

	interface Props {
		selectedFile: StaticAsset | undefined;
		fileTypes?: string[];
	}

	const staticAssetClient: StaticAssetClient = getContext("staticAssetClient");
	const folderClient: FolderClient = getContext("folderClient");
	const workspace: Writable<Workspace | undefined> = getContext("workspace");
	const suite: Writable<Suite> = getContext("suite");
	const brandKit: Writable<BrandKit> = getContext("brandKit");
	const identitiesClient: IdentitiesClient = getContext("identitiesClient");

	let { selectedFile = $bindable(), fileTypes = [] }: Props = $props();

	let searchTerm = $state("");
	let mode: Mode = $state("grid");
	let files: StaticAsset[] = $state([]);
	let folders: Folder[] = $state([]);
	let loading = $state(false);
	let creatorIds: SvelteSet<string> = new SvelteSet();
	let creators: Map<string, User> = $state(new Map<string, User>());
	let assetPagination = $state(
		create(PaginationArgumentsSchema, {
			first: DEFAULT_PAGE_SIZE,
			orderBy: "name",
			order: Order.ASC,
		}),
	);
	const folderPagination = create(PaginationArgumentsSchema, {
		first: DEFAULT_PAGE_SIZE,
		orderBy: "name",
		order: Order.ASC,
	});
	let fileTypeFilter: string | undefined = $state(undefined);
	let assetPageInfo = $state<PaginationPageInfo | undefined>(undefined);
	let folderPageInfo = $state<PaginationPageInfo | undefined>(undefined);
	let folderPath: Folder[] = $state($workspace ? [create(FolderSchema, { orgId: $workspace?.orgId, workspaceId: $workspace?.id, path: ASSETS_FOLDER_PATH })] : []);
	let currentFolderPath = $derived(folderPath[folderPath.length - 1]?.path ?? ASSETS_FOLDER_PATH);

	onMount(() => {
		fetchData();
		mode = localStorage.getItem("files:viewMode") as Mode;
		if (!mode) mode = "grid";
	});

	function toggleView(event: CustomEvent<string>) {
		mode = event.detail as Mode;
		localStorage.setItem("files:viewMode", mode);
	}

	$effect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		assetPagination;
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		categories;
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		folderPath;

		fetchData();
	});

	$effect(() => {
		const ids = new SvelteSet(files.filter(file => file.creator?.userId).map(file => file.creator?.userId ?? ""));
		if (!deepEqual(ids, creatorIds)) {
			creatorIds = ids;
			fetchCreators(Array.from(ids));
		}
	});

	async function fetchCreators(ids: string[]) {
		if (ids.length) {
			const users = await identitiesClient.getUsers(ids);
			creators = new Map(users.map(user => [user.id, user]));
		}
	}

	function getFilteredFileTypes(filter: string | undefined) {
		if (!filter) return fileTypes;
		let allowedTypes: string[] = [];
		if (filter === "Documents") allowedTypes = UPLOADABLE_DOCUMENT_TYPES;
		else if (filter === "Images") allowedTypes = UPLOADABLE_IMAGE_FILE_TYPES;
		else if (filter === "Videos") allowedTypes = UPLOADABLE_MOVIE_TYPES;
		return fileTypes.filter((t: string) => allowedTypes.includes(t));
	}

	function getEmptyStateCopy() {
		if (searchTerm.length) {
			return `No results for "${searchTerm}"`;
		}
		if (fileTypeFilter) {
			return "No files found for filter";
		}
		if (folderPath.length > 1) {
			return "Nothing found in this folder";
		}
		return "Upload your first file";
	}

	function shouldShowFileTypeFilter(types: string[]) {
		if (types.length === 0) return true;
		let categoryCount = 0;
		if (types.some(type => UPLOADABLE_DOCUMENT_TYPES.includes(type))) categoryCount++;
		if (types.some(type => UPLOADABLE_IMAGE_FILE_TYPES.includes(type))) categoryCount++;
		if (types.some(type => UPLOADABLE_MOVIE_TYPES.includes(type))) categoryCount++;
		if (categoryCount > 1) return true;
		return false;
	}

	let emptyStateCopy = $derived(getEmptyStateCopy());
	let categories = $derived(getFilteredFileTypes(fileTypeFilter));
	let showFileTypeFilter = $derived(shouldShowFileTypeFilter(fileTypes));

	function handleInput(e: Event) {
		searchTerm = (e.target as HTMLInputElement).value;
	}

	async function fetchData(scroll?: boolean) {
		if (!$workspace) {
			// We only have data to fetch if we have a workspace but we aren't always guaranteed to be in one
			return;
		}

		loading = true;
		try {
			const shouldFetchFolders = !scroll || folderPageInfo?.hasNextPage;
			const shouldFetchFiles = !scroll || (assetPageInfo?.hasNextPage && !folderPageInfo?.hasNextPage);

			const promises: [Promise<ListStaticAssetsResponse> | null, Promise<FolderList> | null] = [
				shouldFetchFiles ? staticAssetClient.listStaticAssets({ orgId: $suite?.orgId ?? $brandKit?.orgId ?? $workspace.orgId, workspaceId: $workspace.id, namePrefix: searchTerm, pagination: scroll ? (createNextPageArguments(assetPageInfo, "name", Order.ASC) ?? assetPagination) : assetPagination, categories, folderPath: currentFolderPath }) : null,
				shouldFetchFolders ? folderClient.listFolders($workspace.orgId, $workspace.id, currentFolderPath, { namePrefix: searchTerm }, scroll ? (createNextPageArguments(folderPageInfo, "name", Order.ASC) ?? folderPagination) : folderPagination) : null,
			];

			const [staticAssetsResponse, folderResponse] = await Promise.all(promises);

			if (staticAssetsResponse?.staticAssets) {
				if (scroll) {
					files = files.concat(staticAssetsResponse.staticAssets);
				} else {
					files = staticAssetsResponse.staticAssets;
				}
				assetPageInfo = staticAssetsResponse.pageInfo;
			}
			if (folderResponse?.folders) {
				if (scroll) {
					folders = folders.concat(folderResponse.folders);
				} else {
					folders = folderResponse.folders;
				}
				folderPageInfo = folderResponse.pageInfo;
			}
		} catch (e) {
			Logger.error("Could not fetch static assets", { err: e });
		} finally {
			loading = false;
		}
	}

	function selectFile(file: StaticAsset | Folder, checked: boolean) {
		if (isStaticAsset(file)) {
			if (checked) {
				selectedFile = file;
			} else {
				selectedFile = undefined;
			}
		} else {
			folderPath = enterFolder(folderPath, file);
		}
	}

	function getCreator(userId?: string): User | undefined {
		if (!userId || !creators) {
			return;
		}
		return creators.get(userId);
	}

	function clearSearch() {
		searchTerm = "";
	}
</script>

<div class="flex h-full grow flex-col">
	<AssetFolderPath bind:folderPath />
	<div class="flex w-full shrink-0 flex-row items-center justify-between p-1">
		<div use:autofocus class="mr-4 w-full">
			<Search size="md" placeholder="Search" bind:value={searchTerm} oninput={handleInput} clearable classes={{ input: "w-full", svg: "text-primary-600" }} clearableOnClick={clearSearch} />
		</div>
		{#if showFileTypeFilter}
			<FileTypeFilter bind:selectedOption={fileTypeFilter} />
		{/if}
		<DisplayToggle value={mode} on:toggle={toggleView} />
	</div>
	<div class="flex min-h-50 grow flex-col overflow-y-auto">
		{#if files.length === 0 && loading}
			<div class="flex h-full grow flex-col items-center justify-center gap-2">
				<Spinner class="text-center" size={8} />
			</div>
		{:else if (folders.length === 0 && files.length === 0) || (!categories.length && fileTypeFilter && !loading)}
			<div class="py-auto flex grow flex-col items-center justify-center">
				<P size="lg" color="text-black" class="mb-2 text-center font-medium">{emptyStateCopy}</P>
				{#if searchTerm.length < 1}
					<P secondary size="sm" class="text-center">Upload a new file to get started</P>
				{/if}
			</div>
		{:else if mode === "list"}
			<div class="pt-4">
				<Table hoverable divClass="grow overflow-clip border dark:border-gray-600 rounded-lg">
					<TableHead>
						<TableHeadCell class="w-16" />
						<SortableTableHeadCell sortKey="name" bind:pagination={assetPagination} class="w-42">Name</SortableTableHeadCell>
						<SortableTableHeadCell sortKey="createdAt" bind:pagination={assetPagination} class="w-24">Uploaded</SortableTableHeadCell>
						<TableHeadCell class="w-16">Creator</TableHeadCell>
					</TableHead>
					<TableBody tableBodyClass="h-full">
						{#each folders as folder (folder.id)}
							<FileExplorerTableRow item={folder} {selectedFile} {selectFile} {getCreator} />
						{/each}
						{#each files as file (file.id)}
							<FileExplorerTableRow item={file} {selectedFile} {selectFile} {getCreator} />
						{/each}
					</TableBody>
					<InfiniteScroll hasMore={folderPageInfo?.hasNextPage || assetPageInfo?.hasNextPage} threshold={0.1} onLoadMore={() => fetchData(true)} />
				</Table>
			</div>
		{:else}
			<div class="pt-4">
				<Gallery class="grid-cols-5 gap-4 px-1" items={[]}>
					{#each folders as folder (folder.id)}
						<FileExplorerTile item={folder} {selectedFile} {selectFile} />
					{/each}
					{#each files as file (file.id)}
						<FileExplorerTile item={file} {selectedFile} {selectFile} />
					{/each}
					<div class="col-span-full flex h-px">
						<InfiniteScroll hasMore={folderPageInfo?.hasNextPage || assetPageInfo?.hasNextPage} threshold={0.1} onLoadMore={() => fetchData(true)} />
					</div>
				</Gallery>
			</div>
		{/if}
	</div>
</div>
